import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Section } from "@/components/CaseBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Per-case associates: add one to this reference only, or remove one. */
export const CaseAssociates = ({ kase, caseId, user, onDone }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mine = (kase.associates || []).filter((a) => a.advocate_id === user.id);
  const conflicts = (kase.associate_conflicts || []).filter((c) => c.advocate_id === user.id);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post(`/cases/${caseId}/associates`, form);
      toast.success(`${form.email} added to this reference`);
      setForm({ name: "", email: "" });
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/cases/${caseId}/associates/${a.id}`);
      toast.success(`${a.email} removed from this reference`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Section title="Associates on this reference" testId="case-associates-section"
      form={
        <form onSubmit={add} className="grid gap-3 sm:grid-cols-[1fr_1.4fr_auto]" data-testid="case-associate-form">
          <Input required placeholder="Name" value={form.name} data-testid="case-associate-name-input"
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="rounded-none h-10" />
          <Input required type="email" placeholder="associate@chamber.in" value={form.email} data-testid="case-associate-email-input"
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="rounded-none h-10" />
          <Button type="submit" disabled={busy} data-testid="case-associate-add-button"
            className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-10 px-5">
            {busy ? "Adding…" : "Add to this case"}
          </Button>
          {error && <p className="sm:col-span-3 text-sm text-red-800 dark:text-red-400" data-testid="case-associate-error">{error}</p>}
        </form>
      }>
      {mine.length === 0 && conflicts.length === 0 && (
        <p className="px-6 py-6 text-sm text-muted-foreground" data-testid="case-associates-empty">
          No associate on record for your side. Associates registered on your profile appear here automatically.
        </p>
      )}
      {mine.map((a) => (
        <div key={a.id} className="px-6 py-3 border-b border-border last:border-0 flex items-center justify-between gap-4" data-testid={`case-associate-row-${a.id}`}>
          <p className="text-sm">{a.name} <span className="text-muted-foreground font-mono text-xs">· {a.email} · {a.side}'s side</span></p>
          <button onClick={() => remove(a)} data-testid={`case-associate-remove-${a.id}`}
            className="text-red-800 hover:text-red-600 transition-colors duration-200" aria-label="Remove from case">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {conflicts.map((c) => (
        <div key={c.email} className="px-6 py-3 border-t border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-xs" data-testid={`case-associate-conflict-${c.email}`}>
          <span className="font-medium">{c.email}</span> was not attached — {c.reason}.
        </div>
      ))}
    </Section>
  );
};
